/**
 * 🚿 [Phase 56.5 — 2026-07-09] Panneau "Créer une route de lavage"
 * extrait depuis CalendrierProduction.jsx (bloc legacy ~L1674-1806).
 *
 * Rôle : depuis le calendrier, l'utilisatrice ouvre ce panneau pour créer une
 * route de lavage groupant plusieurs BT (avec date, employés assignés,
 * vérification nacelles disponibles).
 *
 * Contrat :
 *   - `routeDate` / `onRouteDateChange(dateStr)` : la date choisie
 *   - `routeEquipe` / `onRouteEquipeChange(idsCsv)` : IDs d'employés séparés par virgule
 *   - `bonsLavage` : liste des BT avec lavage à céduler
 *   - `selectedBons` : Set des bon.id sélectionnés
 *   - `onToggleBon(bonId)` : bascule la sélection d'un bon
 *   - `allEmployees` : liste employés (filtrée localement)
 *   - `nacellesDisponibles` / `nacelleWarning`
 *   - `saving` : booléen savingRoute
 *   - `onClose()`
 *   - `onSubmit()` : appelle handleCreateRoute
 *
 * Aucun changement fonctionnel — pur split JSX.
 */
import { MapPin, X, CheckCircle, Save } from 'lucide-react';

export default function RouteLavagePanel({
  routeDate,
  onRouteDateChange,
  routeEquipe,
  onRouteEquipeChange,
  bonsLavage = [],
  selectedBons,
  onToggleBon,
  allEmployees = [],
  nacellesDisponibles = [],
  nacelleWarning,
  saving,
  onClose,
  onSubmit,
}) {
  const nbSel = selectedBons?.size || 0;
  return (
    <div className="fixed inset-0 z-50 bg-black/30" onClick={() => onClose?.()}>
      <div className="absolute right-0 top-0 h-full w-full max-w-md bg-white shadow-2xl overflow-y-auto" onClick={e => e.stopPropagation()}>
        <div className="sticky top-0 bg-white border-b p-4 flex items-center justify-between z-10">
          <div>
            <h2 className="text-lg font-bold text-slate-900 flex items-center gap-2"><MapPin className="w-5 h-5 text-emerald-600" /> Créer une route de lavage</h2>
            <p className="text-xs text-slate-500">Sélectionnez les bons à inclure dans la route</p>
          </div>
          <button onClick={() => onClose?.()} className="p-1 hover:bg-slate-100 rounded"><X className="w-5 h-5" /></button>
        </div>

        <div className="p-4 space-y-4">
          {/* Date et équipe */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-medium text-slate-700">Date de la route *</label>
              <input
                type="date"
                value={routeDate || ''}
                onChange={e => onRouteDateChange?.(e.target.value)}
                className="w-full mt-1 border rounded-lg px-3 py-2 text-sm"
              />
            </div>
            <div>
              <label className="text-xs font-medium text-slate-700">Employés assignés</label>
              <div className="mt-1 max-h-32 overflow-y-auto border rounded-lg p-2 space-y-1">
                {allEmployees.filter(e => e.est_actif !== false && ['terrain', 'chef_equipe', 'peintre'].includes(e.type_employe || e.role_terrain || '')).length === 0 ? (
                  <p className="text-xs text-slate-400">Aucun employé terrain</p>
                ) : (
                  allEmployees.filter(e => e.est_actif !== false).map(emp => (
                    <label key={emp.id} className="flex items-center gap-2 text-xs cursor-pointer hover:bg-slate-50 p-1 rounded">
                      <input type="checkbox" checked={(routeEquipe || '').includes(emp.id)}
                        onChange={e => {
                          const ids = (routeEquipe || '').split(',').filter(Boolean);
                          if (e.target.checked) ids.push(emp.id);
                          else ids.splice(ids.indexOf(emp.id), 1);
                          onRouteEquipeChange?.(ids.join(','));
                        }} className="rounded" />
                      {emp.prenom} {emp.nom}
                    </label>
                  ))
                )}
              </div>
            </div>
          </div>

          {/* Liste des bons */}
          <div>
            <p className="text-sm font-medium text-slate-700 mb-2">
              Bons avec lavage ({bonsLavage.length}) — {nbSel} sélectionné(s)
            </p>
            {bonsLavage.length === 0 ? (
              <p className="text-sm text-slate-400 italic py-4">Aucun bon de travail avec lavage à céduler.</p>
            ) : (
              <div className="space-y-2 max-h-[50vh] overflow-y-auto">
                {bonsLavage.map(bon => {
                  const selected = selectedBons?.has(bon.id);
                  return (
                    <div key={bon.id}
                      onClick={() => onToggleBon?.(bon.id)}
                      className={`p-3 rounded-lg border cursor-pointer transition-all ${selected ? 'bg-emerald-50 border-emerald-300' : 'bg-white hover:bg-slate-50'}`}
                    >
                      <div className="flex items-center justify-between gap-2">
                        <div className="flex-1 min-w-0">
                          <p className="font-semibold text-sm truncate">
                            {bon.client_nom || '—'}
                            {bon.nacelle_lavage_requise && <span className="ml-1.5 text-cyan-600 text-xs font-normal">🏗 Nacelle</span>}
                          </p>
                          <p className="text-xs text-slate-500 truncate">{bon.adresse_chantier || '—'}</p>
                          {bon.notes_lavage_pression && (
                            <p className="text-xs text-blue-600 mt-0.5 italic truncate">🚿 {bon.notes_lavage_pression}</p>
                          )}
                        </div>
                        <div className="flex items-center gap-2 shrink-0">
                          {bon.jours_restants !== null && bon.jours_restants !== undefined && (
                            <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${bon.jours_restants <= 7 ? 'bg-red-100 text-red-700' : bon.jours_restants <= 14 ? 'bg-amber-100 text-amber-700' : 'bg-slate-100 text-slate-500'}`}>
                              Dans {bon.jours_restants}j
                            </span>
                          )}
                          {selected && <CheckCircle className="w-5 h-5 text-emerald-600" />}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Avertissement nacelle rouge */}
          {nacelleWarning && (
            <div className="flex items-start gap-2 bg-red-50 border border-red-300 rounded-lg px-3 py-2.5 text-sm text-red-700" data-testid="nacelle-warning">
              <span className="text-lg shrink-0">🏗</span>
              <div>
                <p className="font-semibold">Aucune nacelle disponible le {routeDate}</p>
                <p className="text-xs mt-0.5">Un ou plusieurs clients nécessitent une nacelle mais toutes sont déjà assignées ce jour-là. Vous pouvez quand même créer la route.</p>
              </div>
            </div>
          )}
          {/* Info nacelles disponibles */}
          {routeDate && !nacelleWarning && nacellesDisponibles.length > 0 && Array.from(selectedBons || []).some(id => bonsLavage.find(b => b.id === id)?.nacelle_lavage_requise) && (
            <div className="flex items-center gap-2 bg-cyan-50 border border-cyan-200 rounded-lg px-3 py-2 text-sm text-cyan-700">
              <span>🏗</span>
              <p><strong>{nacellesDisponibles.length} nacelle(s) disponible(s)</strong> — sera assignée automatiquement à la création</p>
            </div>
          )}
          <button
            onClick={() => onSubmit?.()}
            disabled={saving || nbSel === 0 || !routeDate}
            className="w-full bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-white py-3 rounded-lg font-medium flex items-center justify-center gap-2"
            data-testid="btn-save-route"
          >
            <Save className="w-4 h-4" />
            {saving ? 'Création...' : `Créer la route (${nbSel} bon${nbSel > 1 ? 's' : ''})`}
          </button>
        </div>
      </div>
    </div>
  );
}
